import { Injectable, LOCALE_ID, inject } from "@angular/core";
import { FilteringExpressionsTree, FilteringLogic, IFilteringExpression, IFilteringExpressionsTree, IgxStringFilteringOperand, SortingDirection } from '@infragistics/igniteui-angular/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { ODataFilterBuilder, ODataFilterFactoryService } from "@tylertech/odata-module";
import { StringHelper, Utilities } from "@tylertech/shared-module";
import { ClassMetadata, IGridViewMetadata, PropertyMetadata } from "@tylertech/tyler-api-types";
import { Observable, Subject } from "rxjs";
import { MetadataKey } from "../../interfaces";
import { CachedCodeLookupService } from "../cachedCodeLookupService";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { StorageService } from "@tylertech/core-module";
import { MetadataFormService } from "../metadataFormService";
import { MetadataService } from "../metadata.service";
import { recentSearchesKey } from "../../constants";
import { GridSortService } from "./grid.sort.service";
import { GridMetadataService } from "./grid.metadata.service";
import { MetadataInputTypeHelperService } from "../metadataInputTypeHelperService";
import { formatDate } from "@angular/common";

export interface LookupFilterList
{
    Key: string;
    Filter: string;
}

export enum FILTER_OPERATION
{
    CONTAINS = "contains",
    STARTS_WITH = "startswith",
    ENDS_WITH = "endswith",
    EQUALS = "eq",
    DOES_NOT_EQUAL = "ne",
    DOES_NOT_CONTAIN = "not contains",
    GREATER_THAN = "gt",
    LESS_THAN = "lt",
    LESS_THAN_EQUAL = "le",
    GREATER_THAN_EQUAL = "ge",
}

@Injectable()
export class GridFilterService
{
    private readonly locale = inject(LOCALE_ID);
    private $storageService = inject(StorageService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private $cachedCodeLookupService = inject(CachedCodeLookupService);
    private $metadataFormService = inject(MetadataFormService);
    private $metadataService = inject(MetadataService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private $gridSortService = inject(GridSortService);
    private $gridMetadataService = inject(GridMetadataService);

    private readonly log = getLoggerFor("GridFilterService");

    searchText: string;

    oDataFilterBuilder(
        providerId: string,
        viewMetadata: IGridViewMetadata,
        skip: number,
        take: number,
        server: boolean,
        model?: any
    ): ODataFilterBuilder
    {
        const self = this;
        const odataFilterBuilder = this.$odataFilterFactory.createBuilder();
        this.applyGridFiltering(odataFilterBuilder, viewMetadata, model);
        const props = this.getSelects(viewMetadata.GridProperties.ClassMetadata);
        const propsWithId = [...props];
        this.pushProps(viewMetadata.GridProperties.IdProperty, propsWithId);
        this.pushProps(viewMetadata.SelectProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanEditProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanDeleteProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanCopyProperty, propsWithId);
        viewMetadata.GridProperties.ClassMetadata.Properties.forEach(p =>
        {
            this.pushProps(p.HiddenProperty, propsWithId);
            self.pushProps(p.DisabledProperty, propsWithId);
            if (p.ActionMetadata)
            {
                self.pushProps(p.ActionMetadata.HiddenProperty, propsWithId);
                self.pushProps(p.ActionMetadata.DisabledProperty, propsWithId);
            }
        });
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.Actions))
        {
            viewMetadata.GridProperties.Actions.forEach(a =>
            {
                if (a.Icon && a.Icon.indexOf("{") === 0)
                {
                    this.pushProps(a.Icon.substring(1, a.Icon.length - 1), propsWithId);
                }
                if (a.Label && a.Label.indexOf("{") === 0)
                {
                    this.pushProps(a.Label.substring(1, a.Label.length - 1), propsWithId);
                }
                this.pushProps(a.HiddenProperty, propsWithId);
            });
        }
        // Without sort properties from select, the grid may not be able to sort properly after Edits
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.Sort))
        {
            this.pushProps(viewMetadata.GridProperties.Sort.Name, propsWithId);
        }
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.SecondarySort))
        {
            this.pushProps(viewMetadata.GridProperties.SecondarySort.Name, propsWithId);
        }
        if (
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata) &&
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata.Properties)
        )
        {
            this.getSelects(viewMetadata.GridProperties.FilterMetadata)
                .forEach(p =>
                {
                    this.pushProps(p, propsWithId);
                });
        }
        // We may need to check other things that are depended on model properties
        odataFilterBuilder.select(propsWithId)
            .count();
        if (!Utilities.IsNullOrUndefined(this.$gridSortService.SortingExpressions[providerId]) && this.$gridSortService.SortingExpressions[providerId].length > 0)
        {
            const expressions: string[] = [];
            this.$gridSortService.SortingExpressions[providerId].forEach(e =>
            {
                if (!Utilities.IsNullOrUndefinedOrEmpty(e.fieldName))
                    expressions.push(`${e.fieldName} ${e.dir === SortingDirection.Desc ? "DESC" : "ASC"}`);
            });
            if (expressions.length > 0) odataFilterBuilder.orderby(expressions.join(","));
        }
        if (server)
        {
            if (take) odataFilterBuilder.top(take);
            if (skip) odataFilterBuilder.skip(skip);
        }
        return odataFilterBuilder;
    }

    buildOdataFilter(filteringTree: IFilteringExpressionsTree, properties: PropertyMetadata[], recentIds: MetadataKey[] = null, idProperty: string = null): Promise<string>
    {
        const self = this;
        const filtersToRun: (() => Promise<LookupFilterList>)[] = [];
        return Promise.resolve(buildOdataFilterImpl(filteringTree, properties, recentIds, idProperty))
            .then(filter =>
            {
                const loaders: Promise<LookupFilterList>[] = [];
                filtersToRun.forEach(f => loaders.push(f()));
                return Promise.all(loaders).then((results: LookupFilterList[]) =>
                {
                    results.forEach(r => filter = filter.replace(`{{${r.Key}}}`, r.Filter));
                    return filter;
                });
            });

        function buildOdataFilterImpl(filteringTree: IFilteringExpressionsTree, properties: PropertyMetadata[], recentIds: MetadataKey[], idProperty: string, initial: boolean = true): string
        {
            let baseQueryString = "";
            let filter = "";

            if (recentIds && idProperty)
            {
                const isNumeric = Number.isInteger(recentIds[0]);
                const safeIds = recentIds.filter(id =>
                    isNumeric
                        ? Number.isInteger(id)
                        : typeof id === "string" && /^[a-zA-Z0-9_-]+$/.test(id)
                );
                const formattedIds = isNumeric ? safeIds : safeIds.map(id => `'${id}'`);
                const recentIdFilter = `(${idProperty} in(${formattedIds.join(",")}))`;
                baseQueryString = self.$odataFilterFactory.createBuilder()
                    .constantFilter(recentIdFilter)
                    .build();
            }
            else if (filteringTree && filteringTree.filteringOperands && filteringTree.filteringOperands.length > 0)
            {
                filteringTree.filteringOperands.forEach(filterOperand =>
                {
                    if (filter !== "") filter += ` ${FilteringLogic[filteringTree.operator].toLowerCase()} `;
                    if (filterOperand instanceof FilteringExpressionsTree)
                    {
                        filter += `(${buildOdataFilterImpl(filterOperand, properties, recentIds, idProperty, false)})`;
                    }
                    else
                    {
                        const propertyMetadata = properties.filter(x => x.Lookup)?.find(x => x.Name === filterOperand.fieldName);
                        if (propertyMetadata)
                        {
                            const filterId = Utilities.generateRandomGuid();
                            filter += `{{${filterId}}}`;
                            filtersToRun.push(() => self.buildLookupFilter(filterId, propertyMetadata, filterOperand as IFilteringExpression));
                        }
                        else
                        {
                            filter += self.buildOdataExpression(filterOperand as IFilteringExpression);
                        }
                    }
                });
            }

            if (!initial) baseQueryString += filter;
            else baseQueryString += filter !== "" ? `$filter=${filter}` : "";

            return baseQueryString;
        }
    }

    buildLookupFilter(filterId: string, propertyMetadata: PropertyMetadata, operand: IFilteringExpression): Promise<LookupFilterList>
    {
        const filter = this.doBuildOdataExpression("Value", operand.searchVal, operand.condition.name, true);
        return this.$cachedCodeLookupService.Get(propertyMetadata.Lookup, filter).then(kvps =>
        {
            const builder = this.$odataFilterFactory
                .createBuilder()
                .in(operand.fieldName, kvps.map(kvp => kvp.Key));
            return <LookupFilterList>{ Key: filterId, Filter: builder.filterString() };
        });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    buildOdataExpression(operand: IFilteringExpression): string
    {
        const value = operand.searchVal;
        if (value instanceof Set)
        {
            let expression = "";
            operand.condition.name = "equals";
            value.forEach(entry =>
            {
                if (expression !== "")
                    expression += ` ${FilteringLogic[1].toLowerCase()} `;
                expression += this.doBuildOdataExpression(operand.fieldName.toString(), entry, operand.condition.name);
            });
            return expression;
        }
        else
            return this.doBuildOdataExpression(operand.fieldName.toString(), value, operand.condition.name);
    }

    applyGridFiltering(odataFilterBuilder: ODataFilterBuilder, viewMetadata: IGridViewMetadata, model?: any): void
    {
        // apply grid filters if available, or use the original constant filter
        if (
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata) &&
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties["$$filterModel"])
        )
        {
            for (const p in viewMetadata.GridProperties["$$filterModel"])
            {
                if (
                    !p.startsWith("$") &&
                    viewMetadata.GridProperties.FilterMetadata.Properties.filter(x => x.Name === p).length > 0
                )
                {
                    odataFilterBuilder.filterEQ(p, viewMetadata.GridProperties["$$filterModel"][p]);
                }
            }
        }
        else if (!Utilities.IsNullOrUndefinedOrEmpty(viewMetadata.ConstantFilter))
        {
            const parentFilter = this.$metadataService.CreateLoadFilter(model, viewMetadata.ChildColumnName, viewMetadata.ParentColumnName, viewMetadata.ConstantFilter);
            if (!Utilities.IsNullOrUndefinedOrEmpty(parentFilter))
                odataFilterBuilder.constantFilter(parentFilter.replace(/^&?\$filter=/, ""));
        }
    }

    private doBuildOdataExpression(fieldName: string, value: Date | number | string, condition: string, buildFilter: boolean = false): string
    {
        const filterValue = typeof value === "number" || value instanceof Date
            ? value
            : value?.toLowerCase() ?? "";

        const builder = this.$odataFilterFactory.createBuilder();

        if (value instanceof Date && condition === "contains")
        {
            const d = new Date(filterValue);
            const startUtc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
            const endUtc   = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()+1)).toISOString();
            builder.constantFilter(`${fieldName} ge ${startUtc} and ${fieldName} lt ${endUtc}`);
        }
        else
        {
            switch (condition)
            {
                case "contains": {
                    builder.contains(`tolower(${fieldName})`, filterValue.toString());
                    break;
                }
                case "startsWith": {
                    // builder.startsWith(`tolower(${fieldName})`, filterValue.toString());
                    builder.startsWith(fieldName, filterValue.toString());
                    break;
                }
                case "endsWith": {
                    // builder.endsWith(`tolower(${fieldName})`, filterValue.toString());
                    builder.endsWith(fieldName, filterValue.toString());
                    break;
                }
                case "equals": {
                    builder.filterEQ(`tolower(${fieldName})`, filterValue);
                    break;
                }
                case "doesNotEqual": {
                    builder.filterNE(`tolower(${fieldName})`, filterValue);
                    break;
                }
                case "doesNotContain": {
                    builder.notContains(`tolower(${fieldName})`, filterValue.toString());
                    break;
                }
                case "greaterThan":
                case "after": {
                    builder.filterGT(fieldName, filterValue);
                    break;
                }
                case "greaterThanOrEqualTo": {
                    builder.filterGE(fieldName, filterValue);
                    break;
                }
                case "lessThan":
                case "before": {
                    builder.filterLT(fieldName, filterValue);
                    break;
                }
                case "lessThanOrEqualTo": {
                    builder.filterLE(fieldName, filterValue);
                    break;
                }
                case "empty": {
                    builder.filterEQ(`length(${fieldName})`, 0);
                    break;
                }
                case "notEmpty": {
                    builder.filterGT(`length(${fieldName})`, 0);
                    break;
                }
                case "null": {
                    builder.filterEQ(fieldName, "null");
                    break;
                }
                case "notNull": {
                    builder.filterNE(fieldName, "null");
                    break;
                }
            }
        }

        if (buildFilter)
            return builder.build();
        return builder.filterString();
    }

    filterLocalTable(tableData: any[], grid: IgxGridComponent): any[]
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(grid.filteringExpressionsTree?.filteringOperands))
            return tableData;
        if (grid?.filterStrategy)
            return grid.filterStrategy.filter(tableData, grid.filteringExpressionsTree);
        return tableData;
    }

    private clearFilter$ = new Subject<void>();
    get clearFilterObservable(): Observable<void>
    {
        return this.clearFilter$.asObservable();
    }

    clearCustomFilter(): void
    {
        this.clearFilter$.next();
    }

    clearFilter(grid: IgxGridComponent): void
    {
        grid.clearFilter();
        grid.filteringLogic = FilteringLogic.And;
    }

    searchFilter(searchText: string, columns: any[], grid: IgxGridComponent): void
    {
        if (searchText === "")
        {
            this.clearFilter(grid);
            return;
        }

        const tree: IFilteringExpressionsTree = new FilteringExpressionsTree(FilteringLogic.Or);
        columns.forEach(column =>
        {
            const expression: IFilteringExpression = <IFilteringExpression>{
                fieldName: column.Name,
                condition: IgxStringFilteringOperand.instance().condition("contains"),
                ignoreCase: true
            };

            if (column.DataType === "percent" || column.DataType === "currency" || column.DataType === "number" || column.DataType === "nonDelineatedNumber")
            {
                const numberSearch = Number(searchText);
                if (!isNaN(numberSearch))
                    expression.searchVal = numberSearch;
            }
            else if (column.DataType === "dateTime" || column.DataType === "date" || column.DataType === "time" || column.DataType === "time24")
            {
                const date = new Date(searchText);
                if (!isNaN(date.valueOf()))
                    expression.searchVal = date;
            }
            else if (column.DataType === "string")
                expression.searchVal = searchText;

            if (!Utilities.IsNullOrUndefined(expression.searchVal))
                tree.filteringOperands.push(expression);
        });

        grid.filteringExpressionsTree = tree;
    }

    allowShowRecent(viewMetadata: IGridViewMetadata): boolean
    {
        return !viewMetadata.SubPropertyName && viewMetadata.GridProperties.IdProperty && this.$metadataFormService.initialForm;
    }

    hasRecent(gridId: string): boolean
    {
        return this.recentIds(gridId).length > 0;
    }

    recentIds(gridId: string): string[]
    {
        const setting = this.getRecentSetting();
        if (setting && setting[gridId])
            return setting[gridId];
        return [];
    }

    addToRecentIds(gridId: string, rowId: string): void
    {
        let setting = this.getRecentSetting();
        if (!setting)
            setting = {};
        if (!setting[gridId])
            setting[gridId] = [rowId];
        else if (setting[gridId].indexOf(rowId) < 0)
            setting[gridId].push(rowId);
        this.$storageService.Set(recentSearchesKey, JSON.stringify(setting));
    }

    private getRecentSetting(): Record<string, string[]>
    {
        let stringSetting = this.$storageService.Get<string>(recentSearchesKey);
        if (!stringSetting)
            stringSetting = null;
        return JSON.parse(stringSetting);
    }

    private getSelects(metadata: ClassMetadata, parent: string = ""): any[]
    {
        let props: any[] = [];
        metadata.Properties.forEach(p =>
        {
            if (p.ClassMetadata) props = props.concat(this.getSelects(p.ClassMetadata, `${p.Name}/`));
            else props.push(`${parent}${p.Name}`);
        });
        return props;
    }

    private pushProps(property: string, props: any[]): void
    {
        if (!!property && props.indexOf(property) === -1) props.push(property);
    }

    parseGridFilter(filteringTree: IFilteringExpressionsTree, classMetadata: ClassMetadata): string
    {
        const self = this;
        let filter = "";
        if (filteringTree && filteringTree.filteringOperands && filteringTree.filteringOperands.length > 0)
        {
            filteringTree.filteringOperands.forEach(filterOperand =>
            {
                if (filter !== "")
                    filter += `${FilteringLogic[filteringTree.operator].toUpperCase()}\n`;
                if (filterOperand instanceof FilteringExpressionsTree)
                    filter += `${this.parseGridFilter(filterOperand, classMetadata)}`;
                else
                {
                    const operand = filterOperand as IFilteringExpression;
                    const property = this.$gridMetadataService.getProperty(classMetadata, operand.fieldName);
                    if (Utilities.IsNullOrUndefined(operand.searchVal))
                        filter += `• ${property.Label ?? operand.fieldName} is ${StringHelper.toStandardCase(operand.conditionName)}\n`;
                    else
                        filter += `• ${property.Label ?? operand.fieldName} ${StringHelper.toStandardCase(operand.conditionName)} ${formatSearchVal(operand.searchVal, property)}\n`;
                }
            });
        }

        function formatSearchVal(searchVal: any, property: PropertyMetadata): string
        {
            if (searchVal instanceof Set)
                return Array.from(searchVal).map(v => formatSearchVal(v, property))
                    .join(", ");
            else if (self.$metadataInputTypeHelperService.isDate(property.InputType))
                return `'${formatDate(searchVal, "MM/dd/yyyy", self.locale)}'`;
            else if (self.$metadataInputTypeHelperService.isDateTime(property.InputType))
                return `'${formatDate(searchVal, "MM/dd/yyyy hh:mm a", self.locale)}'`;
            else if (self.$metadataInputTypeHelperService.isTime(property.InputType))
                return `'${formatDate(searchVal, "hh:mm a", self.locale)}'`;
            else if (self.$metadataInputTypeHelperService.isTime24(property.InputType))
                return `'${formatDate(searchVal, "HH:mm", self.locale)}'`;
            else
            {
                const formatted = self.$metadataService.getFormattedValueFromProp(searchVal, property);
                if (typeof searchVal === "number")
                    return `${formatted}`;
                return `'${formatted}'`;
            }
        }

        return filter;
    }
}
